import { useCallback, useEffect, useState } from 'react';

import { DesktopTreeView } from './components/DesktopTreeView/DesktopTreeView';
import { MobileTreeView } from './components/MobileTreeView/MobileTreeView';

import type { Node, ReviewerNodesProps } from './types';
import type { User } from '@/types/directory/User';

import { useWindowDimensions } from '@/hooks/utils/useWindowsDimensions';

export const MAX_NODES = 8;

export const ReviewerNodes = ({ isMobile, reviewee, reviewers, onRemove }: ReviewerNodesProps) => {
    const dimensions = useWindowDimensions();

    const initialNodes = [
        {
            ...reviewee,
            x: 50,
            y: 50,
            isCenter: true,
            data: reviewee,
        },
    ];

    const [nodes, setNodes] = useState<Node<User>[]>(initialNodes);

    const mobile = isMobile ?? dimensions.isMobile;

    const centerNode: Node<User> | undefined = nodes.find((node) => node.isCenter);
    const allSatellites: Node<User>[] = nodes.filter((node) => !node.isCenter);
    const satelliteNodes: Node<User>[] = allSatellites.slice(0, MAX_NODES);
    const hiddenNodes: Node<User>[] = allSatellites.slice(MAX_NODES);

    const redistributeNodes = useCallback((nodesToDistribute: Node<User>[]) => {
        const centerX = 50;
        const centerY = 50;
        const satelliteCount = nodesToDistribute.length;

        if (satelliteCount === 0) return [];

        const angleStep = (Math.PI * 2) / satelliteCount;
        const baseDistance = 28;

        return nodesToDistribute.map((node, index) => {
            // Add random offset to angle for more organic spacing
            const angleOffset = (Math.random() - 0.5) * 0.4; // Random offset up to ±0.2 radians
            const angle = angleStep * index - Math.PI / 2 + angleOffset;

            // Add more random variation to distance
            const distanceVariation = Math.random() * 8 - 4; // Random offset between -4 and +4
            const distance = baseDistance + distanceVariation;

            return {
                ...node,
                x: centerX + Math.cos(angle) * distance * 1.3,
                y: centerY + Math.sin(angle) * distance * 1.3,
            };
        });
    }, []);

    const addNode = useCallback(
        (user: User) => {
            const newNode: Node<User> = {
                x: 50, // Start at center for animation
                y: 50,
                data: user,
            };

            setNodes((prevNodes) => {
                const center = prevNodes.find((n) => n.isCenter);
                const satellites = prevNodes.filter((n) => !n.isCenter);
                const currentSatelliteCount = satellites.length;

                // If adding a hidden node, do not redistribute
                if (currentSatelliteCount >= MAX_NODES) {
                    return center ? [center, ...satellites, newNode] : [...satellites, newNode];
                }
                // Otherwise, redistribute all visible satellites
                const allSatellites = [...satellites, newNode];
                const redistributed = redistributeNodes(allSatellites);
                return center ? [center, ...redistributed] : [...redistributed];
            });
        },
        [redistributeNodes],
    );

    const removeNode = useCallback(
        (id: string) => {
            const node = nodes.find((n) => n.data?.id === id);
            if (node?.isCenter) {
                return;
            }

            setNodes((prevNodes) => {
                const center = prevNodes.find((n) => n.isCenter);
                const allSatellites = prevNodes.filter((n) => !n.isCenter);
                // Remove the node from satellites
                const remainingSatellites = allSatellites.filter((n) => n.data?.id !== id);
                // If the removed node was hidden, just remove it and keep the rest as-is
                if (
                    allSatellites.length > MAX_NODES &&
                    allSatellites.findIndex((n) => n.data?.id === id) >= MAX_NODES
                ) {
                    return center ? [center, ...remainingSatellites] : [...remainingSatellites];
                }
                // Otherwise, redistribute the visible satellites
                const visibleSatellites = remainingSatellites.slice(0, MAX_NODES);
                const redistributed = redistributeNodes(visibleSatellites);
                const hiddenSatellites = remainingSatellites.slice(MAX_NODES);
                return center
                    ? [center, ...redistributed, ...hiddenSatellites]
                    : [...redistributed, ...hiddenSatellites];
            });

            onRemove(id);
        },
        [nodes, redistributeNodes, onRemove],
    );

    // Sync nodes with reviewers prop from parent (add new, remove deleted)
    useEffect(() => {
        const currentReviewerIds = nodes.filter((n) => !n.isCenter).map((n) => n.data.id);
        const reviewerIds = reviewers.map((r) => r.id);

        // Add new reviewers
        const newReviewers = reviewers.filter((r) => !currentReviewerIds.includes(r.id));
        if (newReviewers.length > 0) {
            newReviewers.forEach(addNode);
        }

        // Remove reviewers that are no longer in the prop
        const removedIds = currentReviewerIds.filter((id) => !reviewerIds.includes(id));
        if (removedIds.length > 0) {
            setNodes((prevNodes) => {
                const center = prevNodes.find((n) => n.isCenter);
                const remainingSatellites = prevNodes.filter(
                    (n) => !n.isCenter && !removedIds.includes(n.data.id),
                );
                const redistributed = redistributeNodes(remainingSatellites);
                return center ? [center, ...redistributed] : [...redistributed];
            });
        }
    }, [reviewers, nodes, addNode, redistributeNodes]);

    if (mobile) {
        return (
            <MobileTreeView
                dataTestId={'mobile-tree-view'}
                centerNode={centerNode}
                satelliteNodes={allSatellites}
                onRemove={removeNode}
            />
        );
    }

    return (
        <DesktopTreeView
            dataTestId={'desktop-tree-view'}
            centerNode={centerNode}
            hasOverflow={hiddenNodes.length > 0}
            allSatelliteNodes={satelliteNodes}
            hiddenNodes={hiddenNodes}
            onRemove={removeNode}
        />
    );
};
